import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
	WritableSignal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { IAttachedFiles, IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { FilterChipComponent } from '@app/shared/filter-components/filter-chip/filter-chip.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { documentTypes } from '@app/pages/cmms/components/files/document-type/document-type.component';
import { FileTypes } from '@app/shared/ui-components/au-document-type/file-types';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { NewFileLoaderService } from '@app/shared/services/new-file-loader.service';
import { formatCmmsLocationsToInput } from '@app/domain/locations/type-translations';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { IDroppedFile } from '@app/shared/ui-components/au-file-loader/au-file-loader.interfaces';

@Component({
	selector: 'files-tab',
	imports: [
		CommonModule,
		MatCardModule,
		MatCheckboxModule,
		MatIconModule,
		MatMenuModule,
		MatButtonModule,
		NgxTolgeeModule,
		FilterChipComponent,
	],
	templateUrl: './files-tab.component.html',
	styleUrls: ['./files-tab.component.scss', '../../../../shared/styles/entity-tabs.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilesTabComponent {
	private readonly filesService = inject(FilesService);
	private readonly notify = inject(IconSnackBarService);
	private readonly translateService = inject(TranslateService);
	private readonly newFileLoaderService = inject(NewFileLoaderService);
	workorder = input<IWorkOrder>();
	filesChanged = output<IAttachedFiles[]>();
	fileTypesConfig: string = this.newFileLoaderService.fileTypesConfigToFileTypes('all');

	fileSelections = new SelectionModel<IAttachedFiles>(
		true,
		[],
		true,
		(a, b) => a.pk === b.pk
	);

	filterVariablesList = computed(() => {
		return documentTypes.map(type => ({
			value: this.getFileType(type).name,
			icon: this.getFileType(type).icon,
		}));
	});

	filteredFiles = computed(() => {
		return this.workorder().files.filter(file => {
			if (this.filter() === null) {
				return this.workorder().files;
			}
			return this.getFileType(file.type).name === this.filter();
		});
	});

	filesForUpload: WritableSignal<File[]> = signal<File[]>([]);
	filesList: WritableSignal<Partial<IDroppedFile>[]> =
		this.newFileLoaderService.filesList;

	filter = signal<string>(null);

	constructor() {
		effect(() => {
			if (
				this.newFileLoaderService.filesFinishedLoading() ===
					this.filesForUpload().length &&
				this.filesForUpload().length > 0
			) {
				const emitUploadedFiles = this.filesList().map(file => ({
					file: {
						url: file.fileMetadata.file.url,
						contentType: file.contentType,
					},
					created: new Date(),
					...file,
				}));
				this.filesChanged.emit([
					...(emitUploadedFiles as IAttachedFiles[]),
					...this.workorder().files,
				]);
				this.filesForUpload.set([]);
				this.newFileLoaderService.resetFilesList();
			}
		});
	}

	fileChanged(event: Event): void {
		const files: File[] = Array.from((event.target as HTMLInputElement).files);
		this.filesForUpload.set(files);
		this.newFileLoaderService.startLoadProcedure(
			files,
			formatCmmsLocationsToInput(this.workorder().locations as ICMMSLocation[])
		);
	}

	unlinkFiles() {
		for (const file of this.fileSelections.selected) {
			this.filesService
				.unlinkAttachedElements({
					pk: file.pk,
					workOrders: this.workorder().pk,
				})
				.subscribe(() => {
					this.notify.success(
						`${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`
					);
				});
		}
	}

	getFileType(type: string): { icon: string; name: string } {
		if (!type) {
			return { icon: 'no-document-type', name: '' };
		}
		return FileTypes[type];
	}
}
